import type { LetterTemplateOption } from '../Contracts/ILetter'

/** Чи шаблон прив'язаний саме до цього власника (точний збіг, Л-11). */
const isBoundTo = (template: LetterTemplateOption, ownerUuid: string | null): boolean => {
    if (ownerUuid === null) return false

    return [...(template.clients ?? []), ...(template.carriers ?? [])].some((item) => item?.uuid === ownerUuid)
}

const num = (value: unknown, fallback: number): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback)

/**
 * Набір шаблонів кнопки «Відправити лист» (`SPEC-013` §6.2, R-05):
 *
 *  - **порядок** — спершу прив'язані до власника, далі загальні; у кожній
 *    групі за «Сортуванням», далі за «Ід» (так і в легасі,
 *    `AddButtonCompanyMailCrud.jsx:71`). Бек відбирає набір (`applicableTo`),
 *    порядок тримаємо тут — він визначає, який шаблон «за замовчуванням»;
 *  - **за замовчуванням** — перший з `isDefault` у цьому порядку: прив'язаний
 *    має пріоритет над загальним. Немає такого — кнопки немає зовсім;
 *  - **інші** — усі, крім обраного за замовчуванням, у тому самому порядку:
 *    їх показує меню «Генерація документу». Другий шаблон з позначкою «За
 *    замовчуванням» (загальний, коли є прив'язаний) теж іде в меню — легасі
 *    його відкидав, і такий шаблон не можна було обрати ніде (вичитка
 *    підказок 2026-10-14, №15).
 */
export const chooseLetterTemplates = (
    templates: readonly LetterTemplateOption[],
    ownerUuid: string | null
): { defaultTemplate: LetterTemplateOption | null; others: LetterTemplateOption[] } => {
    const ordered = [...templates].sort((a, b) => {
        const bound = Number(isBoundTo(b, ownerUuid)) - Number(isBoundTo(a, ownerUuid))

        if (bound !== 0) return bound

        const sorting = num(a.sorting, 0) - num(b.sorting, 0)

        return sorting !== 0 ? sorting : num(a.id, 0) - num(b.id, 0)
    })

    const defaultTemplate = ordered.find((template) => template.isDefault === true) ?? null

    return {
        defaultTemplate,
        others: ordered.filter((template) => template !== defaultTemplate),
    }
}

/**
 * Шаблон листа документа (`SPEC-013` §6.2, останній рядок): перший шаблон
 * провайдера документа за «Сортуванням»; `isDefault` тут не враховується —
 * як у легасі (`Buttons4DocumentDrawerExtra.jsx:37-49`).
 */
export const chooseDocumentLetterTemplate = (templates: readonly LetterTemplateOption[]): LetterTemplateOption | null =>
    [...templates].sort((a, b) => num(a.sorting, 0) - num(b.sorting, 0) || num(a.id, 0) - num(b.id, 0))[0] ?? null
